import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import productData from "../Datas/productData";
import "../css/productdetails.scss";

const colorTones = [
  [/(natur|weiß|kreide|creme|hafer|ivory)/i, "#e8e2d4"],
  [/(schwarz|graphit|basalt)/i, "#303331"],
  [/(nacht|marine|indigo|tiefblau)/i, "#26384b"],
  [/(blau|ozean|fjord|himmel|kornblume)/i, "#6d91ae"],
  [/(wald|pinie|moos|olive|salbei|schilf|eukalyptus)/i, "#66775d"],
  [/(beere|pflaume|altrosa)/i, "#815767"],
  [/(ziegel|rost|terrakotta|zimt|ton)/i, "#a7644b"],
  [/(sand|stein|kamel|karamell|tabak|hasel|geröll)/i, "#a99475"],
  [/(gelb|löwenzahn)/i, "#d3ad45"],
];

const fallbackTones = ["#d6d0c1", "#65745e", "#485b70", "#956958"];

const getColorTone = (color, index) =>
  colorTones.find(([pattern]) => pattern.test(color))?.[1] ||
  fallbackTones[index % fallbackTones.length];

const scoreRecommendation = (candidate, product) => {
  const productTags = new Set(product.tags || []);
  const sharedTags = (candidate.tags || []).filter((tag) => productTags.has(tag));
  return (
    (candidate.category === product.category ? 8 : 0) +
    (candidate.subcategory === product.subcategory ? 6 : 0) +
    sharedTags.length * 2
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const productId = Number(id);
  const product = Number.isInteger(productId)
    ? productData.find((item) => item.id === productId)
    : null;
  const { addToCart, toggleWishlist, isInWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [sizeError, setSizeError] = useState(false);
  const [addStatus, setAddStatus] = useState("idle");
  const [failedImages, setFailedImages] = useState(() => new Set());
  const sizeGroupRef = useRef(null);
  const navigationTimerRef = useRef(null);

  const images = product?.images?.length
    ? product.images
    : product?.image
      ? [product.image]
      : [];
  const firstColor = product?.colors?.[0] || "";
  const colors = product?.colors || [];
  const sizes = product?.size || [];
  const features = product?.Features || [];
  const attributes = product?.attributes || {};
  const descriptions = Array.isArray(product?.description)
    ? product.description
    : product?.description
      ? [product.description]
      : [];

  useEffect(() => {
    if (navigationTimerRef.current) {
      window.clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }
    setSelectedSize("");
    setSelectedColor(firstColor);
    setSelectedImage(0);
    setSizeError(false);
    setAddStatus("idle");
    setFailedImages(new Set());
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [product?.id, firstColor]);

  useEffect(
    () => () => {
      if (navigationTimerRef.current) {
        window.clearTimeout(navigationTimerRef.current);
      }
    },
    []
  );

  if (!product) {
    return (
      <main className="product-detail-page">
        <section className="product-detail-page__not-found" aria-labelledby="not-found-title">
          <p className="product-detail-page__eyebrow">Artikel nicht gefunden</p>
          <h1 id="not-found-title">Dieses Lieblingsstück ist nicht verfügbar.</h1>
          <p>
            Vielleicht wurde der Artikel verschoben oder ist nicht mehr Teil der
            aktuellen Kollektion.
          </p>
          <Link className="product-detail-page__primary-link" to="/shop">
            Kollektion entdecken
          </Link>
        </section>
      </main>
    );
  }

  const priceFormatter = new Intl.NumberFormat(
    siteConfig.locale || "de-DE",
    {
      style: "currency",
      currency: product.currency || siteConfig.currency || "EUR",
    }
  );
  const formattedPrice = priceFormatter.format(Number(product.price) || 0);
  const formattedOriginalPrice =
    Number(product.originalPrice) > Number(product.price)
      ? priceFormatter.format(Number(product.originalPrice))
      : null;
  const wishlistActive = isInWishlist(product.id);
  const recommendations = productData
    .filter((candidate) => candidate.id !== product.id)
    .sort(
      (left, right) =>
        scoreRecommendation(right, product) -
          scoreRecommendation(left, product) || left.id - right.id
    )
    .slice(0, 4);

  const markImageAsFailed = (source) => {
    setFailedImages((current) => {
      const next = new Set(current);
      next.add(source);
      return next;
    });
  };

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    setSizeError(false);
    setAddStatus("idle");
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    setAddStatus("idle");
  };

  const handleAddToCart = (event) => {
    event.preventDefault();
    if (!selectedSize) {
      setSizeError(true);
      setAddStatus("idle");
      window.requestAnimationFrame(() => {
        sizeGroupRef.current?.querySelector("button")?.focus();
      });
      return;
    }

    addToCart(product.id, 1, selectedSize, selectedColor);
    setAddStatus("added");
    navigationTimerRef.current = window.setTimeout(() => {
      navigate("/cartpage", {
        state: {
          justAdded: {
            id: product.id,
            size: selectedSize,
            color: selectedColor,
          },
        },
      });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 900);
  };

  return (
    <main className="product-detail-page">
      <nav
        className="product-detail-page__breadcrumb"
        aria-label="Brotkrümelnavigation"
      >
        <Link to="/">Startseite</Link>
        <span aria-hidden="true">/</span>
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
          {product.category}
        </Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{product.subcategory}</span>
      </nav>

      <section className="product-detail-page__purchase" aria-labelledby="product-title">
        <div className="product-detail-page__gallery">
          <div className="product-detail-page__thumbs" aria-label="Produktbilder">
            {images.map((image, index) => (
              <button
                className={`product-detail-page__thumb${
                  index === selectedImage ? " is-active" : ""
                }`}
                type="button"
                key={`${image}-${index}`}
                onClick={() => setSelectedImage(index)}
                aria-label={`Produktbild ${index + 1} von ${images.length} anzeigen`}
                aria-pressed={index === selectedImage}
              >
                {failedImages.has(image) ? (
                  <span
                    className="product-detail-page__image-fallback product-detail-page__image-fallback--thumb"
                    role="img"
                    aria-label="Bild derzeit nicht verfügbar"
                  >
                    <span aria-hidden="true">◌</span>
                  </span>
                ) : (
                  <img
                    src={image}
                    alt=""
                    loading={index === 0 ? "eager" : "lazy"}
                    decoding="async"
                    onError={() => markImageAsFailed(image)}
                  />
                )}
              </button>
            ))}
          </div>

          <div className="product-detail-page__main-image">
            {images[selectedImage] && !failedImages.has(images[selectedImage]) ? (
              <img
                src={images[selectedImage]}
                alt={`${product.name}, Ansicht ${selectedImage + 1}`}
                decoding="async"
                onError={() => markImageAsFailed(images[selectedImage])}
              />
            ) : (
              <div
                className="product-detail-page__image-fallback"
                role="img"
                aria-label={`${product.name}, Bild derzeit nicht verfügbar`}
              >
                <span aria-hidden="true">◌</span>
                <p>Bild wird vorbereitet</p>
              </div>
            )}
            <span className="product-detail-page__image-counter">
              {Math.min(selectedImage + 1, Math.max(images.length, 1))} / {Math.max(images.length, 1)}
            </span>
          </div>
        </div>

        <aside className="product-detail-page__buy-box">
          <div className="product-detail-page__badges">
            {(product.badges || []).map((badge) => (
              <span key={badge}>{badge}</span>
            ))}
            {attributes.certification && <span>Nachhaltig</span>}
          </div>

          <p className="product-detail-page__brand">{siteConfig.brandName}</p>
          <h1 id="product-title">{product.name}</h1>

          <div
            className="product-detail-page__rating"
            aria-label={`${product.rating} von 5 Sternen bei ${product.reviews} Bewertungen`}
          >
            <span aria-hidden="true">★</span>
            <strong>{product.rating}</strong>
            <span>({product.reviews} Bewertungen)</span>
          </div>

          <div className="product-detail-page__price-group">
            <p className="product-detail-page__price">{formattedPrice}</p>
            {formattedOriginalPrice && <del>{formattedOriginalPrice}</del>}
          </div>
          <p className="product-detail-page__tax-note">
            inkl. MwSt., zzgl. <Link to="/shipping">Versandkosten</Link>
          </p>

          <ul className="product-detail-page__selling-points">
            {features.slice(0, 2).map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>

          <form className="product-detail-page__options" onSubmit={handleAddToCart} noValidate>
            <fieldset className="product-detail-page__color-fieldset">
              <legend>
                Farbe <strong>{selectedColor}</strong>
              </legend>
              <div className="product-detail-page__swatches">
                {colors.map((color, index) => (
                  <button
                    type="button"
                    key={color}
                    className={`product-detail-page__swatch${
                      selectedColor === color ? " is-selected" : ""
                    }`}
                    style={{ "--swatch-color": getColorTone(color, index) }}
                    onClick={() => handleColorSelect(color)}
                    aria-label={`Farbe ${color}`}
                    aria-pressed={selectedColor === color}
                    title={color}
                  >
                    <span aria-hidden="true" />
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset
              className="product-detail-page__size-fieldset"
              ref={sizeGroupRef}
              aria-invalid={sizeError}
              aria-describedby={sizeError ? "product-size-error" : undefined}
            >
              <legend>
                <span>Größe wählen *</span>
                <Link to="/contact">Größenberatung</Link>
              </legend>
              <div className="product-detail-page__sizes">
                {sizes.map((size) => (
                  <button
                    type="button"
                    key={size}
                    className={selectedSize === size ? "is-selected" : ""}
                    onClick={() => handleSizeSelect(size)}
                    aria-pressed={selectedSize === size}
                  >
                    {size}
                  </button>
                ))}
              </div>
              {sizeError && (
                <p className="product-detail-page__option-error" id="product-size-error" role="alert">
                  Bitte wähle zuerst eine Größe aus.
                </p>
              )}
            </fieldset>

            <div className="product-detail-page__actions">
              <button
                className="product-detail-page__add-button"
                type="submit"
                disabled={!product.stock || addStatus === "added"}
              >
                {product.stock
                  ? addStatus === "added"
                    ? "Hinzugefügt ✓"
                    : "In den Warenkorb"
                  : "Zurzeit nicht verfügbar"}
              </button>
              <button
                className={`product-detail-page__wishlist-button${
                  wishlistActive ? " is-active" : ""
                }`}
                type="button"
                onClick={() => toggleWishlist(product.id)}
                aria-pressed={wishlistActive}
                aria-label={
                  wishlistActive
                    ? `${product.name} von der Merkliste entfernen`
                    : `${product.name} zur Merkliste hinzufügen`
                }
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.8 4.7a5.5 5.5 0 0 0-7.8 0L12 5.8l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.4 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" />
                </svg>
                <span>{wishlistActive ? "Gemerkt" : "Merken"}</span>
              </button>
            </div>
            <p className="product-detail-page__add-status" aria-live="polite">
              {addStatus === "added"
                ? "Der Artikel wurde hinzugefügt. Du wirst zum Warenkorb weitergeleitet."
                : ""}
            </p>
          </form>

          <div className="product-detail-page__trust" aria-label="Servicevorteile">
            <Link to="/returns">
              <span aria-hidden="true">↩</span>
              <span>
                <strong>Kostenlose Rücksendung</strong>
                Einfach und ressourcenschonend
              </span>
            </Link>
            <Link to="/returns">
              <span aria-hidden="true">30</span>
              <span>
                <strong>30 Tage Rückgabe</strong>
                In Ruhe anprobieren
              </span>
            </Link>
            <Link to="/shipping">
              <span aria-hidden="true">→</span>
              <span>
                <strong>Verlässlich geliefert</strong>
                Versandkostenfrei ab {priceFormatter.format(siteConfig.freeShippingThreshold || 99)}
              </span>
            </Link>
          </div>
        </aside>
      </section>

      <section className="product-detail-page__story" aria-labelledby="product-story-title">
        <div className="product-detail-page__story-heading">
          <p className="product-detail-page__eyebrow">Fair, natürlich, langlebig</p>
          <h2 id="product-story-title">Erfahre mehr über dieses Lieblingsstück</h2>
        </div>

        <div className="product-detail-page__details">
          <details open>
            <summary>Produktdetails</summary>
            <div className="product-detail-page__detail-content">
              {descriptions.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              <ul>
                {features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
              {product.sku && <small>Art.-Nr. {product.sku}</small>}
            </div>
          </details>

          <details>
            <summary>Material und Pflege</summary>
            <div className="product-detail-page__detail-content">
              <dl>
                <div>
                  <dt>Material</dt>
                  <dd>{attributes.material || product.material}</dd>
                </div>
                <div>
                  <dt>Passform</dt>
                  <dd>{attributes.fit || "Zeitgemäß und bequem"}</dd>
                </div>
                <div>
                  <dt>Pflege</dt>
                  <dd>{attributes.care || "Pflegehinweis am Artikel beachten"}</dd>
                </div>
              </dl>
            </div>
          </details>

          <details>
            <summary>Zertifizierung und Verantwortung</summary>
            <div className="product-detail-page__detail-content product-detail-page__detail-content--certificate">
              <span aria-hidden="true">✓</span>
              <div>
                <strong>{attributes.certification || "Verantwortungsvoll ausgewählte Materialien"}</strong>
                <p>
                  Wir achten auf nachvollziehbare Herkunft, sorgfältige Verarbeitung
                  und Qualitäten, die lange Freude bereiten.
                </p>
              </div>
            </div>
          </details>

          <details>
            <summary>Herkunft</summary>
            <div className="product-detail-page__detail-content">
              <p>
                Hergestellt in <strong>{attributes.origin || "Europa"}</strong>.
                Unsere Partner verbinden handwerkliche Erfahrung mit verlässlichen
                Qualitäts- und Sozialstandards.
              </p>
            </div>
          </details>
        </div>
      </section>

      <section
        className="product-detail-page__recommendations"
        aria-labelledby="recommendations-title"
      >
        <div className="product-detail-page__recommendations-heading">
          <div>
            <p className="product-detail-page__eyebrow">Passt gut dazu</p>
            <h2 id="recommendations-title">Weitere natürliche Lieblingsstücke</h2>
          </div>
          <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
            Alle Artikel ansehen <span aria-hidden="true">→</span>
          </Link>
        </div>

        <div className="product-detail-page__recommendation-grid">
          {recommendations.map((recommendation) => (
            <article key={recommendation.id}>
              <Link
                className="product-detail-page__recommendation-image"
                to={`/shopDetails/${recommendation.id}`}
                aria-label={`${recommendation.name} ansehen`}
              >
                {failedImages.has(recommendation.image) ? (
                  <span
                    className="product-detail-page__image-fallback product-detail-page__image-fallback--card"
                    role="img"
                    aria-label="Bild derzeit nicht verfügbar"
                  >
                    <span aria-hidden="true">◌</span>
                    <small>Natürlich ausgewählt</small>
                  </span>
                ) : (
                  <img
                    src={recommendation.image}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={() => markImageAsFailed(recommendation.image)}
                  />
                )}
                {recommendation.badges?.[0] && (
                  <span className="product-detail-page__recommendation-badge">
                    {recommendation.badges[0]}
                  </span>
                )}
              </Link>
              <div className="product-detail-page__recommendation-copy">
                <p>{recommendation.subcategory}</p>
                <h3>
                  <Link to={`/shopDetails/${recommendation.id}`}>
                    {recommendation.name}
                  </Link>
                </h3>
                <strong>
                  {priceFormatter.format(Number(recommendation.price) || 0)}
                </strong>
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
